import { Badge, ErrorNotice } from '@app/components'
import { http } from '@app/lib/api'
import type { InboxItem, InboxResponse } from '@shared/contracts/inbox'
import { useQuery } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'
import { Link } from 'react-router'

// "FYI" items can be dismissed; the choice is remembered on this device only.
const DISMISSED_KEY = 'bp.inbox.dismissed'
const listeners = new Set<() => void>()
let dismissed: string[] = readDismissed()

function readDismissed(): string[] {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY)
    const v = raw ? JSON.parse(raw) : []
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []
  } catch {
    return []
  }
}

function dismiss(id: string) {
  if (dismissed.includes(id)) return
  dismissed = [...dismissed, id].slice(-100)
  try {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed))
  } catch {
    /* private mode or storage full: dismissed for this visit only */
  }
  for (const l of listeners) l()
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

/** The signed-in person's "Things to do", minus info items dismissed on this device. */
export function useInbox() {
  const q = useQuery({
    queryKey: ['portal', 'inbox'],
    queryFn: () => http.get<InboxResponse>('/api/v1/portal/inbox'),
    staleTime: 60_000,
  })
  const hidden = useSyncExternalStore(
    subscribe,
    () => dismissed,
    () => dismissed,
  )
  const items = (q.data?.items ?? []).filter(
    (i) => i.severity === 'action' || !hidden.includes(i.id),
  )
  return { items, isLoading: q.isLoading, error: q.error, dismiss }
}

/** The detail already says when; past-due action items also get a flag. */
const overdue = (item: InboxItem) =>
  item.severity === 'action' && !!item.dueAt && Date.parse(item.dueAt) < Date.now()

/** The portal overview's "Things to do" card. */
export function ThingsToDo() {
  const { items, isLoading, error, dismiss } = useInbox()
  return (
    <section className="card stack--sm inbox" aria-labelledby="inbox-h">
      <h2 id="inbox-h" className="card__title mb-0">
        Things to do
      </h2>
      {isLoading ? <p className="small muted mb-0">Checking…</p> : null}
      {error ? <ErrorNotice error={error} /> : null}
      {!isLoading && !error && items.length === 0 ? (
        <p className="mb-0">You’re all caught up.</p>
      ) : null}
      {items.length > 0 ? (
        <ul className="inbox__list">
          {items.map((i) => {
            return (
              <li key={i.id} className={`inbox__item inbox__item--${i.severity}`}>
                <div className="inbox__body">
                  <Badge tone={i.severity === 'action' ? 'warning' : 'info'}>
                    {i.severity === 'action' ? 'To do' : 'FYI'}
                  </Badge>{' '}
                  {overdue(i) ? (
                    <>
                      <Badge tone="danger">Overdue</Badge>{' '}
                    </>
                  ) : null}
                  <Link to={i.href} className="bold">
                    {i.title}
                  </Link>
                  <div className="small muted">{i.detail}</div>
                </div>
                {i.severity === 'info' ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    aria-label={`Dismiss: ${i.title}`}
                    onClick={() => dismiss(i.id)}
                  >
                    Dismiss
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
    </section>
  )
}
